import { useState, useEffect, useMemo } from 'react';
import { RETENTION_DAYS, retentionLabel } from '@arcnet/contracts';
import { ExtensionNumberField } from './ExtensionNumber.js';
import { useCan, useOwnOnly } from '../../lib/auth/session.js';
import { useSubroute } from '../../lib/subroute.js';
import { useExtension } from '../../lib/api/hooks-extension.js';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { useExtensionTypes } from '../../lib/api/hooks-products.js';
import { Icon } from '../../components/ui/Icon.js';
import { StatusBadge, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { OrderedPicker } from '../../components/ui/OrderedPicker.js';
import { ExtensionForwardingTab } from './ExtensionForwarding.js';
import { ExtensionHotDeskTab } from './ExtensionHotDesk.js';
import { ExtensionScreeningTab } from './ScreeningTab.js';
import { ExtensionRestrictionsTab } from './ExtensionRestrictions.js';
import { Section, Field, Toggle, FormActions } from '../../components/ui/Form.js';
import { VoicemailTab } from './ExtensionVoicemail.js';
import { MembershipTab, PhoneSetupTab } from './ExtensionTabs.js';
import { CallerIdSelect } from './CallerIdSelect.js';
import { DepartmentSelect } from './DepartmentsTab.js';
import { ExtensionFeaturesTab } from './ExtensionFeaturesTab.js';
import { ExtensionTroubleshoot, type FixView } from './ExtensionTroubleshoot.js';
import { presenceOf } from '../extensions/ExtensionPresence.js';
import type { Extension } from '@arcnet/contracts';

type View = 'details' | 'features' | 'restrictions' | 'forwarding' | 'screening' | 'voicemail'
  | 'recording' | 'membership' | 'phone' | 'hotdesk' | 'troubleshoot';

/** Another seat in the list, by number, to step to without going back to it. */
export interface SeatLink { key: string; label: string }

/**
 * A seat's sections, grouped by the job somebody came to do, and which console
 * each belongs to.
 *
 * The MSP console configures what a seat IS and what it COSTS -- its identity,
 * and the feature set it is entitled to. How the customer then runs that seat
 * day to day is theirs: where their voicemail is emailed, who they forward to,
 * which queues they sit in. Those are in the client portal at
 * /manage/:tenantId, which is the console that already owns IVRs, queues,
 * schedules and routes.
 *
 * `platform: false` is therefore not a permission -- an operator reading into
 * an account can still open the client portal and see it. It is a statement
 * about whose screen the setting belongs on, and the reason the MSP extension
 * page does not grow into a second copy of the customer's own.
 *
 * Listed down the side, not across the top (14 September 2026). Twelve tabs
 * did not fit across a seat's pane: the strip scrolled with its scrollbar
 * hidden, and everything after Hot desking was out of sight -- which is how
 * the SIP password, the MAC address field and the app's QR code came to look
 * as though they had been removed.
 */
const VIEWS: ReadonlyArray<{
  group: string;
  items: ReadonlyArray<readonly [View, string, { platform: boolean }]>;
}> = [
  {
    group: 'Seat',
    items: [
      ['details', 'Details', { platform: true }],
      // Second, not last: what a seat is ALLOWED to do frames everything after it.
      // Dialling restrictions and recording are settings within those features.
      ['features', 'Features', { platform: true }],
      // Dialling restrictions stay on both. They are the spend and fraud ceiling --
      // the same policy `dialcheck` enforces on the call path -- so an operator
      // investigating a bill needs them without opening the customer's console.
      ['restrictions', 'Dialling restrictions', { platform: true }],
    ],
  },
  {
    // What happens to this person's calls. Forwarding and follow-me are greyed
    // out, with the reason, when the seat's type does not include them
    // (FeatureGate) -- and refused by the API and withheld from the switch as
    // well. Screening is not an entitlement, so every seat has it.
    group: 'Calls',
    items: [
      ['forwarding', 'Forwarding', { platform: false }],
      ['screening', 'Screening', { platform: false }],
      ['voicemail', 'Voicemail', { platform: false }],
      ['recording', 'Recording', { platform: false }],
      ['membership', 'Groups & queues', { platform: false }],
    ],
  },
  {
    group: 'Phone',
    items: [
      /**
       * Getting a phone working: the SIP account for setting one up by hand,
       * desk phones by MAC address, and the app's QR code.
       *
       * On BOTH consoles. Provisioning was client-only once, on the reasoning
       * that the person holding the phone is the customer -- true of the QR
       * code, and wrong about everything else: a provider commissioning an
       * order types the MAC addresses, and support asked to re-open a
       * provisioning window cannot do it from a screen without the button.
       */
      ['phone', 'Phone setup', { platform: true }],
      // On both consoles: the provider decides whether a seat has hot desking (the
      // Features section), and support is who resets a PIN or ends a forgotten login.
      ['hotdesk', 'Hot desking', { platform: true }],
      // Last, because it is where you go when the sections before it have not
      // explained the fault -- and each problem it finds links to the section
      // that fixes it. Live registration and failed sign-ins are in it too.
      ['troubleshoot', 'Troubleshoot', { platform: true }],
    ],
  },
];

/** What Troubleshoot can send somebody to fix. Every one is also a section here. */
const FIX_VIEWS: ReadonlyArray<FixView> = ['phone', 'details', 'forwarding', 'restrictions', 'voicemail'];

/**
 * One extension, everything about it.
 *
 * A seat is not a number and a name: it carries outbound presentation, cost
 * attribution, dialling restrictions, recording policy and supervision rules,
 * most of which cost money or carry compliance weight when set wrongly. Split
 * into sections so the common case (name, caller ID) stays on one screen and
 * the rarely-touched controls do not crowd it, with the four facts every visit
 * starts from above all of them.
 */
export function ExtensionDetail({ tenantId, extensionId, onBack, scope = 'tenant', prev, next, onStep }: {
  tenantId: string; extensionId: string; onBack: () => void;
  scope?: 'platform' | 'tenant';
  /** The seats either side of this one, in the list as it was filtered. */
  prev?: SeatLink | undefined;
  next?: SeatLink | undefined;
  onStep?: ((number: string) => void) | undefined;
}) {
  // Self-service: their forwarding and their voicemail, the settings the API lets them change
  // (OWN_EXTENSION_FIELDS, voicemail under extension:write:own).
  const ownOnly = useOwnOnly();
  // Each section asks the API under its own permission; one the role lacks is not offered, and
  // one it can see but not change is shown read-only (permission sweep, 19 September 2026).
  const can = useCan();
  const readable = (key: string): boolean =>
    key === 'voicemail' ? ownOnly || can('voicemail:read') || can('voicemail:manage')
      : key === 'phone' ? can('device:read') || can('extension:credentials:reveal')
        : true;
  const writable = (key: string): boolean =>
    ownOnly ? true
      : key === 'voicemail' ? can('voicemail:manage')
        : key === 'phone' || key === 'troubleshoot' || key === 'membership' ? true
          : can('extension:write');
  const groups = useMemo(() => VIEWS
    .map((g) => ({
      group: g.group,
      items: g.items.filter(([key, , where]) => (ownOnly
        // Their forwarding, their voicemail, and setting up their own phone (23 September 2026).
        ? key === 'forwarding' || key === 'voicemail' || key === 'phone'
        : (scope === 'tenant' || where.platform) && readable(key))),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    .filter((g) => g.items.length > 0), [scope, ownOnly, can]);
  const offered = useMemo(
    () => new Set<string>(groups.flatMap((g) => g.items.map(([k]) => k))), [groups]);

  /**
   * The open section is the last part of the path -- .../extensions/1004/phone
   * -- so a link names both the seat and the section, and a refresh keeps the
   * place. A section this console does not offer, such as a client-portal link
   * opened in the MSP console, lands on Details rather than on nothing.
   */
  const route = useSubroute();
  const asked = route.rest[0];
  const view: View = asked && offered.has(asked) ? asked as View
    : offered.has('details') ? 'details' : ([...offered][0] ?? 'details') as View;
  // Replaces: back returns to the list, not through every section opened.
  const setView = (v: View): void => route.go(v === 'details' ? [] : [v], { replace: true });

  // Refetched every ten seconds while Troubleshoot is open, which is where
  // registration is watched.
  const ext = useExtension(tenantId, extensionId, view === 'troubleshoot');

  const fixes: Partial<Record<FixView, () => void>> = {};
  for (const k of FIX_VIEWS) if (offered.has(k)) fixes[k] = () => setView(k);

  return (
    <>
      <button type="button" className="arc-crumb"
        onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={15} /> Extensions
      </button>

      <div className="arc-page-head">
        <div className="arc-row" style={{ gap: 9, minWidth: 0 }}>
          <h1 className="arc-h1 arc-truncate">
            <span className="arc-mono">{ext.data?.number}</span>
            {ext.data ? ` · ${ext.data.display_name}` : ''}
          </h1>
          {/* Only when it is NOT active. "Active" is the state every seat is
              in, so a pill saying so is a pill that is always there and never
              read -- and it makes the one that matters, a suspended seat,
              look like more of the same. Same rule the list follows. */}
          {ext.data && ext.data.status !== 'active' && (
            <StatusBadge status={ext.data.status} />
          )}
          {ext.data?.extension_type_name && (
            <Badge tone="info">{ext.data.extension_type_name}</Badge>
          )}
        </div>
        {onStep && (prev || next) && (
          <div className="arc-row" style={{ gap: 2 }}>
            <StepButton to={prev} which="Previous" icon="chevronUp" onStep={onStep} />
            <StepButton to={next} which="Next" icon="chevronDown" onStep={onStep} />
          </div>
        )}
      </div>

      {ext.error ? <ErrorBanner error={ext.error} /> : (
        <>
          {ext.data
            ? <SeatFacts ext={ext.data} onSetUp={() => setView('phone')} />
            : (
              <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
                <div className="arc-skeleton" style={{ height: 40 }} />
              </div>
            )}

          <div className="arc-seat">
            <nav className="arc-card arc-seat-nav" aria-label="Extension settings">
              {groups.map((g) => (
                <div key={g.group} className="arc-seat-nav-group">
                  <div className="arc-rail-section">{g.group}</div>
                  {g.items.map(([k, label]) => (
                    <button key={k} type="button" className="arc-rail-item"
                      aria-current={view === k ? 'page' : undefined}
                      onClick={() => setView(k)}>
                      {label}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            {/*
              Keyed by seat. Stepping to the next one would otherwise keep each
              section's state, and a SIP password or QR code revealed for the
              last seat would stay on screen under this one's name.
            */}
            <div key={extensionId} style={{ minWidth: 0 }}>
              {!ext.data ? (
                <div className="arc-card arc-card-body">
                  <div className="arc-skeleton" style={{ height: 90 }} />
                </div>
              ) : (
                <fieldset disabled={!writable(view)} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                  {view === 'details'      && <DetailsTab tenantId={tenantId} ext={ext.data} scope={scope} />}
                  {view === 'features'     && <ExtensionFeaturesTab tenantId={tenantId} ext={ext.data} />}
                  {view === 'restrictions' && <ExtensionRestrictionsTab tenantId={tenantId} ext={ext.data} />}
                  {view === 'forwarding'   && <ExtensionForwardingTab tenantId={tenantId} ext={ext.data} scope={scope} />}
                  {view === 'screening'    && <ExtensionScreeningTab tenantId={tenantId} ext={ext.data} scope={scope} />}
                  {view === 'voicemail'    && <VoicemailTab tenantId={tenantId} ext={ext.data} />}
                  {view === 'recording'    && <RecordingTab tenantId={tenantId} ext={ext.data} />}
                  {view === 'membership'   && <MembershipTab tenantId={tenantId} ext={ext.data} />}
                  {view === 'phone'        && <PhoneSetupTab tenantId={tenantId} ext={ext.data} selfService={ownOnly} />}
                  {view === 'hotdesk'      && <ExtensionHotDeskTab tenantId={tenantId} ext={ext.data} scope={scope} />}
                  {view === 'troubleshoot' && <ExtensionTroubleshoot tenantId={tenantId} ext={ext.data} open={fixes} />}
                </fieldset>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/** Up and down rather than left and right: they move through a list that runs top to bottom. */
function StepButton({ to, which, icon, onStep }: {
  to: SeatLink | undefined; which: 'Previous' | 'Next';
  icon: 'chevronUp' | 'chevronDown'; onStep: (id: string) => void;
}) {
  const label = to ? `${which} extension: ${to.label}` : `No ${which.toLowerCase()} extension`;
  return (
    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
      disabled={!to} title={label} aria-label={label}
      onClick={() => { if (to) onStep(to.key); }}>
      <Icon name={icon} size={15} />
    </button>
  );
}

/**
 * What every visit starts from, above every section: is the phone up, which
 * phone is it, what number does it show, and will it ring.
 *
 * Each was a tab away, and two of them caught people out on the same morning --
 * a seat still presenting the demo data's placeholder caller ID, and SIP details
 * nobody could find (14 September 2026).
 */
function SeatFacts({ ext, onSetUp }: { ext: Extension; onSetUp: () => void }) {
  const p = presenceOf(ext);
  const reg = ext.registration;

  return (
    <div className="arc-card arc-seat-facts">
      <div>
        <div className="arc-stat-label">Status</div>
        <div className="arc-seat-fact-value">
          <Badge tone={p.tone}><span className="arc-dot" aria-hidden="true" />{p.label}</Badge>
          {p.detail && (
            <span className="arc-subtle arc-truncate" style={{ fontSize: 12 }}>{p.detail}</span>
          )}
        </div>
      </div>

      <div>
        <div className="arc-stat-label">Phone</div>
        <div className="arc-seat-fact-value">
          {reg?.user_agent
            ? <span className="arc-truncate" title={reg.contact_ip ?? undefined}>{reg.user_agent}</span>
            : reg
              ? <span className="arc-subtle">unknown</span>
              : (
                <button type="button" className="arc-btn arc-btn-sm" onClick={onSetUp}>
                  Set up a phone
                </button>
              )}
        </div>
      </div>

      <div>
        <div className="arc-stat-label">Caller ID</div>
        <div className="arc-seat-fact-value">
          {ext.hide_caller_id
            ? <span className="arc-subtle">withheld</span>
            : ext.outbound_caller_id_number
              ? <span className="arc-mono arc-truncate">{ext.outbound_caller_id_number}</span>
              : <span className="arc-subtle">account default</span>}
        </div>
      </div>

      <div>
        <div className="arc-stat-label">Incoming calls</div>
        <div className="arc-seat-fact-value">
          {ext.do_not_disturb
            ? <Badge tone="warn">do not disturb</Badge>
            : ext.forward_always
              ? <span className="arc-truncate">forwarded to <span className="arc-mono">{ext.forward_always}</span></span>
              : <span>{ext.forward_busy || ext.forward_no_answer ? 'rings, then forwards' : 'rings here'}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Tracks a draft against the server copy and reports whether it differs.
 *
 * Only CHANGED keys are sent: a PATCH that echoes every field back would
 * clobber a concurrent edit by another operator with values this form never
 * touched.
 */
function useDraft<T extends Record<string, unknown>>(source: T) {
  const [draft, setDraft] = useState<Partial<T>>({});
  useEffect(() => { setDraft({}); }, [source]);

  const set = <K extends keyof T>(key: K, value: T[K]) =>
    setDraft((d) => {
      const next = { ...d };
      if (Object.is(source[key], value)) delete next[key];
      else next[key] = value;
      return next;
    });

  const value = <K extends keyof T>(key: K): T[K] =>
    (key in draft ? draft[key] : source[key]) as T[K];

  const dirty = Object.keys(draft).length > 0;
  return { draft, set, value, dirty, reset: () => setDraft({}) };
}

/**
 * What this build actually advertises, and why you would pick one.
 *
 * Matches global_codec_prefs in vars.xml exactly. G.729 is compiled but
 * `arc_fs_enable_g729` is false, so it is deliberately absent -- offering it
 * would be a control the switch never honours.
 */
const CODECS = [
  { key: 'PCMA', note: 'G.711 a-law — the default here, uncompressed' },
  { key: 'PCMU', note: 'G.711 µ-law — same, US convention' },
  { key: 'G722', note: 'wideband, better on a desk phone that supports it' },
  { key: 'OPUS', note: 'best over a poor link; softphones and mobile' },
] as const;

function DetailsTab({ tenantId, ext, scope }: {
  tenantId: string; ext: Extension; scope: 'platform' | 'tenant';
}) {
  const save = useUpdateExtension(tenantId);
  const types = useExtensionTypes();
  const d = useDraft(ext as unknown as Record<string, unknown>);

  const str = (k: string) => (d.value(k) as string | null) ?? '';
  const bool = (k: string) => Boolean(d.value(k));

  return (
    <>
      <Section title="Identity" description="How this extension appears internally.">
        <Field label="Display name" htmlFor="d-name">
          <input id="d-name" className="arc-input" value={str('display_name')}
            onChange={(e) => d.set('display_name', e.target.value)} />
        </Field>
        {/* Shown, with a separate Change number step for the provider (ExtensionNumber.tsx). */}
        <ExtensionNumberField tenantId={tenantId} ext={ext} />
        {/*
          Seat type is shown everywhere and editable only from the MSP console.
          It sets the feature set AND the monthly charge, so a customer changing
          it from their own portal would be changing their own bill. The API
          refuses it outside platform scope regardless — this is the half that
          stops somebody trying.
        */}
        <Field label="Seat type" htmlFor="d-type"
          hint={scope === 'platform'
            ? 'Determines the feature set and the monthly charge.'
            : 'Determines the feature set and the monthly charge, so it is set by your provider. Ask them to change it.'}>
          <select id="d-type" className="arc-select" value={str('extension_type_id')}
            disabled={scope !== 'platform'}
            onChange={(e) => d.set('extension_type_id', e.target.value || null)}>
            <option value="">— none —</option>
            {types.data?.data.filter((t) => t.is_active).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Caller ID"
        description="What the far end sees when this extension calls out.">
        <CallerIdSelect
          id="d-cid" tenantId={tenantId}
          number={(d.value('outbound_caller_id_number') as string | null) ?? null}
          anonymous={bool('hide_caller_id')}
          onChange={(next) => {
            // Both halves of one decision, set together: a seat that withholds
            // its number has no number to present, and the old pair of controls
            // allowed exactly that contradiction to be saved.
            d.set('outbound_caller_id_number', next.number);
            d.set('hide_caller_id', next.anonymous);
          }}
        />
        <Field label="Caller ID name" htmlFor="d-cidname"
          hint="Rarely shown on a carrier call; most networks display the number only.">
          <input id="d-cidname" className="arc-input" value={str('outbound_caller_id_name')}
            onChange={(e) => d.set('outbound_caller_id_name', e.target.value || null)} />
        </Field>
        <Field label="Emergency caller ID" htmlFor="d-ecid"
          hint={str('emergency_caller_id_number')
            ? 'Presented on emergency calls. Emergency services locate the caller from the address the carrier holds for this number.'
            : "Not set: emergency calls present this seat's caller ID, and are located by that number's address."}>
          <input id="d-ecid" className="arc-input arc-mono"
            value={str('emergency_caller_id_number')} placeholder="+27115550100"
            onChange={(e) => d.set('emergency_caller_id_number', e.target.value || null)} />
        </Field>
      </Section>

      <Section title="Cost attribution" description="Where this seat's usage is reported.">
        <Field label="Account / agent code" htmlFor="d-acct"
          hint="Appears on the CDR for cost-centre reporting.">
          <input id="d-acct" className="arc-input" value={str('account_code')}
            onChange={(e) => d.set('account_code', e.target.value || null)} />
        </Field>
        <Field label="Department" htmlFor="d-group" hint="For spend reports and dialling restrictions.">
          <DepartmentSelect id="d-group" tenantId={tenantId} value={str('admin_group_id')}
            onChange={(id) => d.set('admin_group_id', id || null)} />
        </Field>
        <Field label="Spend cap" htmlFor="d-budget"
          hint="Outbound calls are refused once exceeded.">
          <input id="d-budget" className="arc-input" inputMode="decimal"
            value={str('budget_amount')} placeholder="0.00"
            onChange={(e) => d.set('budget_amount', e.target.value || null)} />
        </Field>
        <Field label="Cap period" htmlFor="d-period">
          <select id="d-period" className="arc-select" value={str('budget_period')}
            onChange={(e) => d.set('budget_period', e.target.value || null)}>
            <option value="">— no cap —</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </Field>
      </Section>

      {/*
        Rarely changed, so after the sections people come here to change. The
        codecs and prompt language used to open this page, inside Identity,
        ahead of the caller ID almost everybody was looking for.
      */}
      <Section title="Call handling">
        <Field label="Phone engine" htmlFor="d-engine"
          hint="How this seat connects: a physical handset, the browser, or a mobile app.">
          <select id="d-engine" className="arc-select" value={str('phone_engine')}
            onChange={(e) => d.set('phone_engine', e.target.value)}>
            <option value="external">External handset</option>
            <option value="webrtc">Browser (WebRTC)</option>
            <option value="mobile">Mobile app</option>
            <option value="softphone">Desktop softphone</option>
          </select>
        </Field>
        <Field label="No-answer timeout" htmlFor="d-timeout" hint="Seconds before the call moves on.">
          <input id="d-timeout" className="arc-input" inputMode="numeric"
            value={String(d.value('call_timeout_sec') ?? 30)}
            onChange={(e) => d.set('call_timeout_sec', Number(e.target.value) || 30)} />
        </Field>
        <Field label="Concurrent call limit" htmlFor="d-max">
          <input id="d-max" className="arc-input" inputMode="numeric"
            value={String(d.value('max_concurrent_calls') ?? 4)}
            onChange={(e) => d.set('max_concurrent_calls', Number(e.target.value) || 1)} />
        </Field>
        <Field label="Language" htmlFor="d-lang" hint="Prompt language for this seat.">
          <input id="d-lang" className="arc-input" value={str('language')}
            placeholder="en-ZA" onChange={(e) => d.set('language', e.target.value || null)} />
        </Field>
        {/*
          Codecs: follow the switch, or an ORDERED choice for this seat.

          The order is the whole meaning -- the switch offers them first to last
          (absolute-codec-string) -- so they are chosen in order, not ticked.
          The ticks this replaced let you pick codecs but not rank them, which
          was the one part that decides what a call actually uses. Removing the
          last codec returns the seat to following the switch: the API refuses
          an empty list precisely so it cannot mean "no codecs".
        */}
        <Field label="Audio codecs" span
          hint={(d.value('allowed_codecs') as string[] | null)
            ? 'Offered to the handset first to last. Pin codecs only to work around a specific handset or a poor link.'
            : `The switch offers ${CODECS.map((c) => c.key).join(', ')}, in that order, which is right for almost every seat.`}>
          <div className="arc-stack" style={{ gap: 10 }}>
            <Segmented label="Codec preference"
              options={[['switch', 'Follow the switch'], ['seat', 'Choose for this seat']] as const}
              value={(d.value('allowed_codecs') as string[] | null) ? 'seat' : 'switch'}
              onChange={(v) => d.set('allowed_codecs',
                v === 'switch' ? null : ((d.value('allowed_codecs') as string[] | null) ?? [CODECS[0].key]))} />
            {(d.value('allowed_codecs') as string[] | null) && (
              <OrderedPicker mono laterLabel="then"
                items={CODECS.map((c) => ({ id: c.key, label: c.key, detail: c.note }))}
                value={(d.value('allowed_codecs') as string[] | null) ?? []}
                onChange={(next) => d.set('allowed_codecs', next.length > 0 ? next : null)} />
            )}
          </div>
        </Field>
        <Toggle id="d-video" label="Video calling" checked={bool('video_enabled')}
          onChange={(v) => d.set('video_enabled', v)} />
        <Toggle id="d-fax" label="Fax (T.38)"
          hint="Enables fax passthrough. Leave off unless a fax machine is attached — it changes codec negotiation."
          checked={bool('fax_enabled')} onChange={(v) => d.set('fax_enabled', v)} />
        <Toggle id="d-dir" label="Show in company directory"
          checked={bool('directory_visible')} onChange={(v) => d.set('directory_visible', v)} />
        <Toggle id="d-dnd" label="Do not disturb"
          checked={bool('do_not_disturb')} onChange={(v) => d.set('do_not_disturb', v)} />
      </Section>

      <Section title="Supervision & security">
        <Toggle
          id="d-spy" label="Prevent monitoring"
          hint="Blocks whisper, listen and barge on this extension. Required for HR, legal and director lines."
          checked={bool('prevent_spy')} onChange={(v) => d.set('prevent_spy', v)}
        />
        <Toggle
          id="d-auth" label="Require PIN for outbound calls"
          hint="For shared handsets in public areas — the caller must identify themselves before dialling out."
          checked={bool('require_authentication')}
          onChange={(v) => d.set('require_authentication', v)}
        />
      </Section>

      <FormActions
        dirty={d.dirty} saving={save.isPending}
        onReset={d.reset}
        onSave={() => save.mutate({ id: ext.id, ...d.draft }, { onSuccess: d.reset })}
      />
    </>
  );
}

function RecordingTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const save = useUpdateExtension(tenantId);
  const d = useDraft(ext as unknown as Record<string, unknown>);
  const mode = (d.value('record_mode') as string) ?? 'none';

  /**
   * Each hint says what the switch actually does, not what the mode is called.
   *
   * "Inbound" and "outbound" include calls between colleagues -- decided, and
   * worth saying, because most people read "inbound" as "from customers".
   *
   * On demand was shown and not selectable until 25 September 2026, because a mode that saves and
   * records nothing is the false assurance this screen exists to avoid. It records now: *1 starts
   * a recording mid-call and stops one, and a call answered through a hunt list or a queue is
   * recorded by the policy of the seat that answered it.
   */
  const MODES: Array<[string, string, string]> = [
    ['none', 'Not recorded', 'No calls on this extension are captured.'],
    ['all', 'All calls', 'Every call this extension places or receives, including calls between colleagues.'],
    ['inbound', 'Received calls', 'Every call this extension receives, including from colleagues, and calls that reach it through a hunt list or a queue.'],
    ['outbound', 'Placed calls', 'Every call this extension places, including to colleagues.'],
    ['on_demand', 'On demand', 'Nothing is recorded until somebody presses *1 during a call. The same key stops it; what was already recorded is kept.'],
  ];

  return (
    <>
      <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
        <Icon name="warning" size={14} />
        <div>
          <div style={{ fontWeight: 600 }}>Recording carries legal obligations</div>
          <div style={{ fontSize: 12 }}>
            Under RICA the parties must be aware, and under POPIA a recording is personal
            information with a retention limit and a subject-access right. Set retention
            deliberately — indefinite storage is a liability, not a safety net (doc 22).
          </div>
        </div>
      </div>

      <Section title="What gets recorded" columns={1}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {MODES.map(([v, label, hint]) => (
            <label key={v} className="arc-row"
              style={{ gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="radio" name="rec-mode" value={v} checked={mode === v}
                onChange={() => d.set('record_mode', v)} style={{ marginTop: 2 }} />
              <div className="arc-stack">
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</span>
                <span className="arc-hint" style={{ marginTop: 0 }}>{hint}</span>
              </div>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Retention">
        <Field label="Keep recordings for" htmlFor="rec-days"
          hint="Overrides the account's retention for this extension. A change applies to recordings already kept.">
          <select id="rec-days" className="arc-input" disabled={mode === 'none'}
            value={String(d.value('record_retention_days') ?? '')}
            onChange={(e) => d.set('record_retention_days',
              e.target.value ? Number(e.target.value) : null)}>
            <option value="">Account default</option>
            {RETENTION_DAYS.map((n) => <option key={n} value={String(n)}>{retentionLabel(n)}</option>)}
          </select>
        </Field>
      </Section>

      <FormActions
        dirty={d.dirty} saving={save.isPending}
        onReset={d.reset}
        onSave={() => save.mutate({ id: ext.id, ...d.draft }, { onSuccess: d.reset })}
      />
    </>
  );
}
